import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { tokenStorage } from '@/api/client'
import { queryClient } from '@/lib/queryClient'
import type { AgencyView } from '@/api/types'

export type Role = 'PLATFORM_ADMIN' | 'AGENCY_ADMIN' | 'AGENT'

export interface AuthUser {
  agentId: string
  agencyId: string | null
  email: string
  fullName: string
  role: Role
  /** The user's own email signature — the composer previews it so a send holds no surprises. */
  emailSignature?: string | null
}

interface AuthState {
  user: AuthUser | null
  agency: AgencyView | null
  isAuthenticated: boolean
  setSession: (
    user: AuthUser,
    agency: AgencyView | null,
    accessToken: string,
    refreshToken: string,
  ) => void
  setAgency: (agency: AgencyView) => void
  /** Refresh the stored identity from the server (GET /me) without touching tokens or cached data —
   *  so a rename / role / branding change shows up without a re-login. No-op if signed out. */
  refreshIdentity: (user: AuthUser, agency: AgencyView | null) => void
  clearSession: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      agency: null,
      isAuthenticated: false,
      setSession: (user, agency, accessToken, refreshToken) => {
        // A new session may be a DIFFERENT workspace (same email, another agency): drop every
        // cached query so no data from the previous workspace lingers without a page refresh.
        // Only ever called on login/register (token refresh writes tokenStorage directly), so this
        // never wipes data mid-session.
        queryClient.clear()
        tokenStorage.set(accessToken, refreshToken)
        set({ user, agency, isAuthenticated: true })
      },
      setAgency: (agency) => set({ agency }),
      refreshIdentity: (user, agency) =>
        // Only while signed in — guards against a late /me response landing after sign-out.
        set((state) => (state.isAuthenticated ? { user, agency } : state)),
      clearSession: () => {
        queryClient.clear() // don't leave the signed-out workspace's data for the next sign-in
        tokenStorage.clear()
        set({ user: null, agency: null, isAuthenticated: false })
      },
    }),
    {
      name: 'profitpipe.auth',
      partialize: (state) => ({
        user: state.user,
        agency: state.agency,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
